import React, { useMemo, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, ButtonBase, CircularProgress } from '@mui/material';
import { useQueries } from '@tanstack/react-query';

import httpInstances from '@/api/axiosInstance';
import type { SymbolPreviewItem } from '@/components/SymbolsPreview';
import SymbolsPreview from '@/components/SymbolsPreview';
import {
  extractGxCodesFromMessage,
  gxQueryKey,
  toCatalogueItem
} from '@/features/AIChat/AskGXDrawer/utils/gxCodes';
import type { Message } from '@/features/AIChat/types/chat';

const { axiosDataInstance } = httpInstances;

const MAX_PREVIEW_ITEMS = 5;

const SymbolsPreviewSection = ({ msg, isVisible }: { msg: Message; isVisible: boolean }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const gxCodes = useMemo(() => extractGxCodesFromMessage(msg), [msg]);
  const trimmedGxCodes = useMemo(
    () => gxCodes.slice(0, isExpanded ? undefined : MAX_PREVIEW_ITEMS),
    [gxCodes, isExpanded]
  );
  const queries = useMemo(
    () =>
      trimmedGxCodes.map(code => ({
        queryKey: gxQueryKey(code),
        queryFn: async () => {
          const resp = await axiosDataInstance.get(`/catalogue?Code=${code}`, {
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              fields: 'Title,Code,Alias,Currency,Units,PeriodType,Period,TimeRef'
            }
          });
          return toCatalogueItem(resp.data);
        },
        enabled: trimmedGxCodes.length > 0
      })),
    [trimmedGxCodes]
  );

  const results = useQueries({
    combine: rs => ({
      data: rs.map(r => r.data as SymbolPreviewItem),
      pending: rs.some(r => r.isPending),
      success: rs.every(r => r.isSuccess)
    }),
    queries
  });

  if (!isVisible || !gxCodes.length) return null;

  if (results.pending) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <>
      <SymbolsPreview
        forChat
        enableQuery={false}
        items={results.data}
      />
      {gxCodes.length > MAX_PREVIEW_ITEMS && (
        <Box
          sx={{
            backgroundColor: trimmedGxCodes.length % 2 === 0 ? '#0D1228' : 'rgb(18, 24, 54)',
            px: '16px'
          }}
        >
          <ButtonBase
            sx={{
              color: '#E84E0F'
            }}
            onClick={() => {
              setIsExpanded(prev => !prev);
            }}
          >
            {isExpanded ? 'See less' : 'See more'}
            <ExpandMoreIcon sx={{ rotate: isExpanded ? '180deg' : '0deg', width: '14px' }} />
          </ButtonBase>
        </Box>
      )}
    </>
  );
};

export default SymbolsPreviewSection;
